import React from 'react';
import {StyleSheet, View} from 'react-native';

import Text from '../../components/ui-controls/Text';
import {field, FIELD_ERROR_COLOR, resolveWeight, universal} from '../tokens';

/**
 * Label / required marker / description / error chrome from the LMG UI Library
 * "Field States" frame (file JwxvfWAYFSi5JBQ67QYxOG, node 444:6223), with no
 * input and no box.
 *
 * FormField renders an input inside this frame. The create-account form also
 * has to put the same chrome around a date-of-birth picker and an address
 * entry component, neither of which is a text input, and the design spec
 * forbids faking a FormField around them.
 *
 * Colours were measured off the Field States frame and are not brand
 * primaries. Measured 2026-09-22 across the four brand theme files under
 * assets/common/theme, PRIMARY_COLOUR is #0054A6 (Bottlemart), #FFDD00
 * (SipnSave), #000000 (Harry Brown) and #A0CE66 (Thirsty Camel). Binding this
 * chrome to it would give SipnSave a yellow required marker and a yellow error
 * line on a white field, about 1.3:1 against the surface.
 */

export interface FieldFrameProps {
  label?: string;
  required?: boolean;
  description?: string;
  /** Forces the error chrome. Pair with errorMessage when there is copy to show. */
  error?: boolean;
  errorMessage?: string;
  children?: React.ReactNode;
  testID?: string;
}

// Frame chrome colours are LMG UI Library Field States values. They must NOT
// be repointed at the per-brand theme (see the note above). REQUIRED_COLOR and
// FIELD_TEXT read the brand-neutral `universal` colour tokens.
const REQUIRED_COLOR = universal.discountRed;
const FIELD_TEXT = universal.black;
const ERROR_MESSAGE_COLOR = FIELD_ERROR_COLOR;

const regularStyle = resolveWeight('regular');

/**
 * Builds the accessibilityHint shared by FormField and any other control
 * wrapped in a FieldFrame. Returns undefined when there is nothing to say, so
 * the result can go straight to accessibilityHint.
 */
export function fieldAccessibilityHint({
  required,
  description,
  error,
  errorMessage,
}: {
  required?: boolean;
  description?: string;
  error?: boolean;
  errorMessage?: string;
}): string | undefined {
  const hint = [required ? 'Required' : undefined, description, error ? errorMessage : undefined]
    .filter(Boolean)
    .join('. ');

  return hint || undefined;
}

const FieldFrame = ({
  label,
  required = false,
  description,
  error = false,
  errorMessage,
  children,
  testID = 'form-field',
}: FieldFrameProps) => (
  <View testID={testID} style={styles.root}>
    {label ? (
      <Text variant="body2" style={[styles.label, regularStyle]} testID={`${testID}-label`}>
        {label}
        {required ? (
          <Text
            variant="body2"
            style={styles.required}
            accessibilityLabel="required"
            testID={`${testID}-required`}>
            {' *'}
          </Text>
        ) : null}
      </Text>
    ) : null}
    {description ? (
      <Text variant="caption" style={styles.description} testID={`${testID}-description`}>
        {description}
      </Text>
    ) : null}
    {children}
    {error && errorMessage ? (
      <Text variant="caption" style={styles.errorMessage} testID={`${testID}-error`}>
        {errorMessage}
      </Text>
    ) : null}
  </View>
);

FieldFrame.displayName = 'FieldFrame';

// SHORTCUT: spacing literals - description.marginBottom 8 and
// errorMessage.marginTop 6 in `styles` below, from the LMG UI Library Field
// States frame. Ceiling: the frame's description and error line only. Upgrade
// path: swap them for space tokens in a later DIS-1735 pass.
const styles = StyleSheet.create({
  root: {
    width: '100%',
  },
  label: {
    color: FIELD_TEXT,
    marginBottom: field.labelGap,
  },
  required: {
    color: REQUIRED_COLOR,
  },
  description: {
    color: FIELD_TEXT,
    marginBottom: 8,
  },
  errorMessage: {
    color: ERROR_MESSAGE_COLOR,
    marginTop: 6,
  },
});

export default FieldFrame;
